import { useCallback, useEffect, useMemo, useState } from 'react';

import { InitialInventorizationPallet, configApi } from '../services/configApi';

/** SSCC identifies a pallet globally (uq_pallet_sscc), so a key can only occur once. */
const indexBySscc = (
  pallets: InitialInventorizationPallet[]
): Map<string, InitialInventorizationPallet> => {
  const bySscc = new Map<string, InitialInventorizationPallet>();

  for (const pallet of pallets) {
    if (pallet.sscc !== null) bySscc.set(pallet.sscc, pallet);
  }

  return bySscc;
};

/**
 * A pallet number is only unique within a company, and this set spans companies — it holds
 * the terminal's pallets plus every pallet not yet assigned to an order. A number carried by
 * more than one of them identifies nothing, so it is indexed as ambiguous rather than
 * resolved to whichever row the database returned last.
 */
const indexByExtId = (pallets: InitialInventorizationPallet[]) => {
  const byExtId = new Map<string, InitialInventorizationPallet>();
  const ambiguousExtIds = new Set<string>();

  for (const pallet of pallets) {
    const extId = pallet.ext_id;
    if (extId === null) continue;

    if (byExtId.has(extId)) {
      byExtId.delete(extId);
      ambiguousExtIds.add(extId);
    } else if (!ambiguousExtIds.has(extId)) {
      byExtId.set(extId, pallet);
    }
  }

  return { byExtId, ambiguousExtIds };
};

/**
 * The whole pallet set of the terminal, held in memory for the duration of an initial
 * inventorization: a scan is matched against it without a round trip per pallet.
 */
export const useInitialInventorizationPallets = (
  terminalId: number | undefined,
  enabled: boolean
) => {
  const [pallets, setPallets] = useState<InitialInventorizationPallet[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [hasLoadFailed, setHasLoadFailed] = useState(false);
  const [reloadCount, setReloadCount] = useState(0);

  useEffect(() => {
    if (!enabled || !terminalId) {
      // An aborted load never reaches its own cleanup, so the state it left behind is
      // dropped here: the next inventorization starts from a fresh set, not from this one.
      setPallets([]);
      setIsLoading(false);
      setHasLoadFailed(false);
      return;
    }

    const controller = new AbortController();
    setIsLoading(true);
    setHasLoadFailed(false);

    configApi.getInitialInventorizationPallets(terminalId, controller.signal)
      .then((data) => {
        if (controller.signal.aborted) return;
        setPallets(data);
      })
      .catch((err) => {
        if (controller.signal.aborted) return;
        console.error('Failed to fetch initial inventorization pallets', err);
        setPallets([]);
        setHasLoadFailed(true);
      })
      .finally(() => {
        if (controller.signal.aborted) return;
        setIsLoading(false);
      });

    return () => controller.abort();
  }, [terminalId, enabled, reloadCount]);

  const reload = useCallback(() => setReloadCount((count) => count + 1), []);

  const palletBySscc = useMemo(() => indexBySscc(pallets), [pallets]);
  const { byExtId, ambiguousExtIds } = useMemo(() => indexByExtId(pallets), [pallets]);

  const findPalletBySscc = useCallback(
    (sscc: string) => palletBySscc.get(sscc),
    [palletBySscc]
  );
  const findPalletByExtId = useCallback(
    (extId: string) => byExtId.get(extId),
    [byExtId]
  );
  const isExtIdAmbiguous = useCallback(
    (extId: string) => ambiguousExtIds.has(extId),
    [ambiguousExtIds]
  );

  return { findPalletBySscc, findPalletByExtId, isExtIdAmbiguous, isLoading, hasLoadFailed, reload };
};
